import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import {
  trackPhoneClick,
  trackWhatsAppClick,
  trackEmailClick,
  trackFileDownload,
  trackJobApplication,
} from "@/lib/analytics";

/**
 * Delegated global click listener for outbound contact / file links.
 *
 * Survives route changes because it is attached once to `document` inside a
 * component rendered at the app root. On every route change the effect
 * re-runs (location dependency keeps it fresh) but the listener is cleaned
 * up and re-attached, so there is never more than one listener.
 *
 * Matched link types (no PII is sent):
 *  - tel:        -> phone_click
 *  - wa.me /     -> whatsapp_click
 *    whatsapp.com
 *  - mailto:     -> email_click
 *  - .pdf        -> file_download (file_name derived from the URL)
 *
 * Careers `job_application` events are fired from this listener when an apply
 * link carries a `data-apply-role` attribute (set by the Careers page with the
 * role title). Generic email/whatsapp events are skipped for those links so
 * the more specific `job_application` event is the only one sent.
 */
export default function AnalyticsListener() {
  const location = useLocation();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest("a");
      if (!target) return;
      const href = target.getAttribute("href") || "";
      if (!href) return;

      // Careers apply links carry the role title — fire job_application.
      const applyRole = target.getAttribute("data-apply-role");
      if (applyRole) {
        const method: "email" | "whatsapp" = href.startsWith("mailto:")
          ? "email"
          : "whatsapp";
        trackJobApplication(applyRole, method);
        return;
      }

      // tel: links
      if (href.startsWith("tel:")) {
        trackPhoneClick(href);
        return;
      }

      // WhatsApp links (wa.me or api.whatsapp.com / web.whatsapp.com)
      if (href.includes("wa.me/") || href.includes("whatsapp.com/")) {
        trackWhatsAppClick(href);
        return;
      }

      // mailto: links
      if (href.startsWith("mailto:")) {
        trackEmailClick(href);
        return;
      }

      // PDF file downloads
      if (/\.pdf(\?|$)/i.test(href)) {
        const fileName = href.split("/").pop()?.split("?")[0] || "document.pdf";
        trackFileDownload(fileName, href);
        return;
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname]);

  return null;
}
